import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { FaUserCircle } from "react-icons/fa";
import { BsBuilding } from "react-icons/bs";
import Swal from "sweetalert2";
import PerfilAcessoService from "../../main/services/PerfilAcessoService";
const PROFILES_REQUIRING_ORGAO = new Set([
  "perfil-conselho_tutelar",
  "perfil-servico_acolhimento",
  "perfil-ministerio_publico",
  "perfil-defensoria_publica",
  "perfil-tribunal_justica",
]);

const RadioDot = ({ selected, disabled }) => (
  <Box
    flexShrink={0}
    w="18px"
    h="18px"
    borderRadius="50%"
    border="2px solid"
    borderColor={selected ? (disabled ? "gray.400" : "#1E6164") : "gray.300"}
    display="flex"
    alignItems="center"
    justifyContent="center"
    bg="white"
  >
    {selected && (
      <Box
        w="10px"
        h="10px"
        borderRadius="50%"
        bg={disabled ? "gray.400" : "#1E6164"}
      />
    )}
  </Box>
);

const ProfileSelectionModal = ({
  groups,
  orgaosMap,
  onConfirm,
  onClose,
  isFromLogin,
}) => {
  const singleProfile = groups?.length === 1;

  const [selectedGroupId, setSelectedGroupId] = useState(
    singleProfile ? groups[0].id : null,
  );
  const [selectedOrgaoId, setSelectedOrgaoId] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const selectedGroup = groups?.find((g) => g.id === selectedGroupId) ?? null;
  const currentOrgaos = selectedGroupId
    ? (orgaosMap?.[selectedGroupId] ?? [])
    : [];
  const singleOrgao = currentOrgaos.length === 1;

  // Cenário 6: limpar órgão ao trocar perfil; Cenário 10: auto-selecionar único órgão
  useEffect(() => {
    if (singleOrgao) {
      setSelectedOrgaoId(currentOrgaos[0].id);
    } else {
      setSelectedOrgaoId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId]);

  const selectedOrgao =
    currentOrgaos.find((o) => o.id === selectedOrgaoId) ?? null;

  // Cenário 12: perfil exige órgão mas nenhum compatível
  const profileRequiresOrgao =
    selectedGroup && PROFILES_REQUIRING_ORGAO.has(selectedGroup.name);
  // Cenário 8/9/11/12: validação para habilitar Confirmar
  // Se não há órgãos compatíveis, permite confirmar com orgao=null
  const canConfirm =
    !!selectedGroup &&
    (!profileRequiresOrgao || currentOrgaos.length === 0 || !!selectedOrgao);

  const handleProfileSelect = (groupId) => {
    if (singleProfile) return;
    if (groupId === selectedGroupId) return;
    setSelectedGroupId(groupId);
  };

  const handleOrgaoSelect = (orgId) => {
    if (singleOrgao) return;
    setSelectedOrgaoId(orgId);
  };

  // Cenário 17/18: fechar com confirmação se veio do login
  const handleClose = () => {
    if (isFromLogin) {
      Swal.fire({
        title: "Confirmar saída",
        text: "Ao fechar, você será desconectado do sistema. Deseja continuar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sim, sair",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#169499",
        cancelButtonColor: "#d3d3d3",
      }).then((result) => {
        if (result.isConfirmed) onClose();
      });
    } else {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm || isConfirming) return;
    setIsConfirming(true);
    try {
      const res = await PerfilAcessoService.verificarCompatibilidade(
        selectedGroup.name,
        selectedOrgao?.tipoId ?? 0,
      );
      if (res.data?.valido === false) {
        Swal.fire({
          title: "Erro!",
          text: res.data?.mensagem || "Perfil e Órgão incompatíveis.",
          icon: "error",
          confirmButtonText: "Fechar",
          confirmButtonColor: "#169499",
        });
        return;
      }
      onConfirm(selectedGroup, selectedOrgao);
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text:
          error.response?.data?.mensagem ||
          error.response?.data?.message ||
          "Erro ao verificar compatibilidade.",
        icon: "error",
        confirmButtonText: "Fechar",
        confirmButtonColor: "#169499",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal
      isOpen
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="2xl"
    >
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent borderRadius="16px" p={8} maxW="740px" mx={4}>
        <ModalBody p={0}>
          {/* Header */}
          <Text fontSize="xl" fontWeight="700" color="gray.800" mb={1}>
            Selecione o Perfil e o Órgão
          </Text>
          <Text fontSize="sm" color="gray.500" mb={7}>
            Escolha um perfil e um órgão para continuar
          </Text>

          {/* Two-column body */}
          <Grid templateColumns="1fr 1fr" gap={6}>
            {/* ── PERFIL COLUMN ── */}
            <GridItem>
              <Flex align="center" gap={2} mb={3}>
                <FaUserCircle size={20} color="#1E6164" />
                <Text fontWeight="600" fontSize="sm" color="gray.700">
                  Perfil
                </Text>
              </Flex>

              <Flex flexDir="column" gap={2} maxH="300px" overflowY="auto">
                {groups?.map((group) => {
                  const isSelected = selectedGroupId === group.id;
                  const isDisabled = singleProfile;
                  return (
                    <Flex
                      key={group.id}
                      align="center"
                      gap={3}
                      p="12px 14px"
                      border="1px solid"
                      borderColor={isSelected ? "teal.400" : "gray.200"}
                      borderRadius="8px"
                      bg={
                        isSelected
                          ? isDisabled
                            ? "#F0F8F7"
                            : "#EAF5F4"
                          : "white"
                      }
                      cursor={isDisabled ? "default" : "pointer"}
                      onClick={() => handleProfileSelect(group.id)}
                      _hover={
                        !isDisabled
                          ? { borderColor: "teal.300", bg: "#F5FAFA" }
                          : {}
                      }
                      transition="all 0.15s"
                    >
                      <RadioDot selected={isSelected} disabled={isDisabled} />
                      <Text
                        fontSize="sm"
                        fontWeight={isSelected ? "600" : "400"}
                        color={isDisabled ? "gray.500" : "gray.700"}
                      >
                        {group.name}
                      </Text>
                    </Flex>
                  );
                })}
              </Flex>
            </GridItem>

            {/* ── ÓRGÃO COLUMN ── */}
            <GridItem>
              <Flex align="center" gap={2} mb={3}>
                <BsBuilding size={20} color="#1E6164" />
                <Text fontWeight="600" fontSize="sm" color="gray.700">
                  Órgão
                </Text>
              </Flex>

              {/* Cenário 7 / 12: sem órgãos */}
              {selectedGroup && currentOrgaos.length === 0 ? (
                <Flex
                  p="12px 14px"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="8px"
                  bg="gray.100"
                  align="center"
                  justify="center"
                  minH="52px"
                >
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    fontWeight="500"
                    textAlign="center"
                  >
                    Sem vínculos com Órgãos, entre em contato com o Gestor
                    MCA.
                  </Text>
                </Flex>
              ) : !selectedGroup ? (
                /* Nenhum perfil ainda selecionado */
                <Flex
                  p="12px 14px"
                  border="1px dashed"
                  borderColor="gray.200"
                  borderRadius="8px"
                  bg="gray.50"
                  align="center"
                  justify="center"
                  minH="52px"
                >
                  <Text fontSize="sm" color="gray.400">
                    Selecione um perfil
                  </Text>
                </Flex>
              ) : (
                /* Lista de órgãos */
                <Flex flexDir="column" gap={2} maxH="300px" overflowY="auto">
                  {currentOrgaos.map((orgao) => {
                    const isSelected = selectedOrgaoId === orgao.id;
                    const isDisabled = singleOrgao;
                    return (
                      <Flex
                        key={orgao.id}
                        align="center"
                        gap={3}
                        p="12px 14px"
                        border="1px solid"
                        borderColor={isSelected ? "teal.400" : "gray.200"}
                        borderRadius="8px"
                        bg={
                          isSelected
                            ? isDisabled
                              ? "#F0F8F7"
                              : "#EAF5F4"
                            : "white"
                        }
                        cursor={isDisabled ? "default" : "pointer"}
                        onClick={() => handleOrgaoSelect(orgao.id)}
                        _hover={
                          !isDisabled
                            ? { borderColor: "teal.300", bg: "#F5FAFA" }
                            : {}
                        }
                        transition="all 0.15s"
                      >
                        <RadioDot selected={isSelected} disabled={isDisabled} />
                        <Flex flexDir="column">
                          <Text
                            fontSize="sm"
                            fontWeight={isSelected ? "600" : "400"}
                            color={isDisabled ? "gray.500" : "gray.700"}
                          >
                            {orgao.nome}
                          </Text>
                          {orgao.tipo && (
                            <Text fontSize="xs" color="gray.400" mt="1px">
                              {orgao.tipo}
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                    );
                  })}
                </Flex>
              )}
            </GridItem>
          </Grid>

          {/* Footer buttons */}
          <Flex justify="flex-end" gap={3} mt={8}>
            <Button
              variant="outline"
              borderColor="gray.300"
              color="gray.700"
              px={7}
              borderRadius="8px"
              onClick={handleClose}
              _hover={{ bg: "gray.50" }}
            >
              Fechar
            </Button>
            <Button
              bg={canConfirm ? "#1E6164" : "gray.300"}
              color="white"
              px={8}
              borderRadius="8px"
              isDisabled={!canConfirm || isConfirming}
              isLoading={isConfirming}
              onClick={handleConfirm}
              _hover={{ bg: canConfirm ? "#174F52" : "gray.300" }}
              _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
            >
              Confirmar
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ProfileSelectionModal;
